/** /v2/console/blacklist_rules/ — delivery blacklist rules (system + tenant scoped).
 *
 * Every mutation carries a mandatory `reason` stored in the dedicated change log
 * and the console audit log. Lists are page/page_size paginated (PageNumberPagination).
 */
import type { WireTagChipRead, WireTagChipWrite } from '@/components/TagConditionInput';

import { apiGet, apiPatch, apiPost, client } from './client';
import { blacklistTagLookups, type TagRegistryResponse } from './tagLookups';
import type { TourOperatorEnvironment } from './tourOperators';

export interface UserBrief {
  id: number;
  email: string | null;
  name: string | null;
}

export interface EventGroupBrief {
  id: number;
  name: string;
}

export interface OtaBrief {
  id: number;
  name: string;
}

export interface TourOperatorBrief {
  id: number;
  code: string;
  name: string;
  /** `Tour_Operator.kind` — the Tenant cell marks a sandbox (plan D8). */
  environment?: TourOperatorEnvironment | null;
}

export type DeliveryType = 'email' | 'sms' | 'whatsapp' | 'airbnb';
export type MatchType = 'exact' | 'substring' | 'regex' | 'user' | 'ota' | 'reseller' | 'tag';

export type TagMatchMode = 'and' | 'or';

export interface BlacklistRuleRow {
  id: number;
  scope: 'system' | 'tenant';
  tour_operator: TourOperatorBrief | null;
  delivery_type: DeliveryType;
  delivery_type_display: string;
  match_type: MatchType;
  match_type_display: string;
  pattern: string | null;
  user: UserBrief | null;
  ota: OtaBrief | null;
  /** match_type='tag' only (null otherwise): the ordered tag-condition chips. */
  tags: WireTagChipRead[] | null;
  /** match_type='tag' only: 'and' = every chip must be present, 'or' = any one. */
  tag_match_mode: TagMatchMode | null;
  /** match_type='tag' only: which entities' tags count as present in the context. */
  tag_sources: string[] | null;
  block_all_events: boolean;
  event_codes: string[];
  event_groups: EventGroupBrief[];
  exception_event_codes: string[];
  exception_event_groups: EventGroupBrief[];
  is_active: boolean;
  comment: string | null;
  redirect_to_email: string | null;
  redirect_reply_to_email: string | null;
  redirect_append_conversation_sid: boolean;
  created_ts: string | null;
  updated_ts: string | null;
  created_by: UserBrief | null;
  updated_by: UserBrief | null;
  created_ip: string | null;
  updated_ip: string | null;
}

export interface BlacklistRuleChangeRow {
  id: number;
  rule_id_denorm: number | null;
  action: 'created' | 'updated' | 'deleted';
  reason: string;
  changed_fields: Record<string, unknown> | null;
  snapshot: Record<string, unknown> | null;
  actor: UserBrief | null;
  actor_ip: string | null;
  source: string | null;
  created_ts: string;
}

export interface BlacklistRuleWriteInput {
  tour_operator?: number | 'system' | null;
  delivery_type: DeliveryType;
  match_type: MatchType;
  pattern?: string | null;
  user?: number | null;
  ota?: number | null;
  /** match_type='tag': required on create (1..50), replaces the list on update. */
  tags?: WireTagChipWrite[];
  tag_match_mode?: TagMatchMode;
  /** match_type='tag': required non-empty on create. Codenames from tagSources(). */
  tag_sources?: string[];
  block_all_events: boolean;
  event_codes?: string[];
  event_group_ids?: number[];
  exception_event_codes?: string[];
  exception_event_group_ids?: number[];
  comment?: string | null;
  is_active?: boolean;
  redirect_to_email?: string | null;
  redirect_reply_to_email?: string | null;
  redirect_append_conversation_sid?: boolean;
  reason: string;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface BlacklistListParams {
  page?: number;
  page_size?: number;
  ordering?: string;
  search?: string;
  tour_operator?: string | number; // 'system' | one id or a comma list ('12,845', D1) | undefined (all)
  delivery_type?: string;
  match_type?: string;
  is_active?: string;
}

const BASE = '/blacklist_rules/';

export const blacklistsApi = {
  list(params: BlacklistListParams) {
    return apiGet<PaginatedResponse<BlacklistRuleRow>>(BASE, {
      params: {
        page: params.page,
        page_size: params.page_size,
        ordering: params.ordering || undefined,
        search: params.search || undefined,
        tour_operator: params.tour_operator ?? undefined,
        delivery_type: params.delivery_type || undefined,
        match_type: params.match_type || undefined,
        is_active: params.is_active || undefined,
      },
    });
  },
  retrieve(id: number) {
    return apiGet<BlacklistRuleRow>(`${BASE}${id}/`);
  },
  create(body: BlacklistRuleWriteInput) {
    return apiPost<BlacklistRuleRow>(BASE, body);
  },
  update(id: number, body: Partial<BlacklistRuleWriteInput>) {
    return apiPatch<BlacklistRuleRow>(`${BASE}${id}/`, body);
  },
  remove(id: number, reason: string) {
    // DELETE with a body (axios supports it) so the mandatory reason is recorded.
    return client.delete(`${BASE}${id}/`, { data: { reason } }).then((r) => r.data);
  },
  history(id: number, params?: { page?: number; page_size?: number }) {
    return apiGet<PaginatedResponse<BlacklistRuleChangeRow>>(`${BASE}${id}/history/`, {
      params: { page: params?.page, page_size: params?.page_size },
    });
  },
  eventCodes() {
    return apiGet<string[]>(`${BASE}event_codes/`);
  },
  otas(search: string) {
    return apiGet<OtaBrief[]>(`${BASE}otas/`, { params: { search: search || undefined } });
  },
  /** Tag-source + match-mode registries for the tag-condition builder. */
  tagSources(): Promise<TagRegistryResponse> {
    return blacklistTagLookups.registry();
  },
};
